import { useMemo } from 'react';
import { Box, Column, Row, Tooltip, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { CandidateStatus, CandidateStatusMapper } from '~/domain/PermanentJobs';
import { Colors } from '~/themes/colors';

const TOOLTIP_MAP: Record<CandidateStatus, string> = {
  [CandidateStatus.Applied]:
    'The candidate has active, pending applications for onsite interviews for this job.',
  [CandidateStatus.AppExpired]:
    'The candidate applied for onsite interviews, but all applications have expired.',
  [CandidateStatus.Cancelled]:
    'The candidate applied for onsite interviews, but all applications have expired.',
  [CandidateStatus.Hired]: 'This candidate has been hired for this job.',
  [CandidateStatus.Interviewed]:
    'The candidate has completed at least one onsite interview for this job.',
  [CandidateStatus.Rejected]: 'This candidate has been rejected for this job.',
  [CandidateStatus.Scheduled]:
    'The candidate is scheduled for one or more onsite interviews for this job.',
};

const SEGMENT_FILL_COUNT: Record<CandidateStatus, number> = {
  [CandidateStatus.Applied]: 1,
  [CandidateStatus.AppExpired]: 1,
  [CandidateStatus.Cancelled]: 1,
  [CandidateStatus.Hired]: 3,
  [CandidateStatus.Interviewed]: 3,
  [CandidateStatus.Rejected]: 3,
  [CandidateStatus.Scheduled]: 2,
};

const SEGMENT_COLOR: Record<CandidateStatus, string> = {
  [CandidateStatus.Applied]: colors.secondary[500],
  [CandidateStatus.AppExpired]: colors.warning[500],
  [CandidateStatus.Cancelled]: colors.warning[500],
  [CandidateStatus.Hired]: colors.success[500],
  [CandidateStatus.Interviewed]: colors.secondary[500],
  [CandidateStatus.Rejected]: colors.neutral[400],
  [CandidateStatus.Scheduled]: colors.secondary[300],
};

const TOTAL_SEGMENTS = 3;

interface IProcessStepCellProps {
  status: CandidateStatus;
}

export const ProcessStepCell = ({ status }: IProcessStepCellProps) => {
  const fillCount = SEGMENT_FILL_COUNT[status];
  const segmentColor = SEGMENT_COLOR[status];
  const tooltipText = TOOLTIP_MAP[status];

  const fmtLabel = useMemo(() => {
    const label = CandidateStatusMapper.toProcessStep(status);

    // TODO: Update directly on `CandidateStatusMapper.toProcessStep` once the FF is 100% rolled out
    if (status === CandidateStatus.Applied) {
      return 'Applied';
    }

    // TODO: Update directly on `CandidateStatusMapper.toProcessStep` once the FF is 100% rolled out
    if (status === CandidateStatus.Cancelled) {
      return CandidateStatusMapper.toProcessStep(CandidateStatus.AppExpired);
    }

    return label;
  }, [status]);

  return (
    <Tooltip
      content={
        <Typography as="span" bold color={colors.shades[0]}>
          {tooltipText}
        </Typography>
      }
      delay={250}
      placement="bottom"
    >
      <Column gap={1} style={{ marginLeft: 12 }}>
        <Row gap={1}>
          {Array.from({ length: TOTAL_SEGMENTS }).map((_, index) => (
            <Box
              key={index}
              height="8px"
              width="20px"
              className="rounded-lg"
              style={{ backgroundColor: index < fillCount ? segmentColor : Colors.neutral_250 }}
            />
          ))}
        </Row>
        <Typography as="span" color={colors.neutral[500]}>{fmtLabel}</Typography>
      </Column>
    </Tooltip>
  );
};
